if (typeof debounce === 'undefined') {
  var debounce = function(fn, wait) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  };
}
/**
 *  @class
 *  @function ThemeHeader
 */
if (!customElements.get('theme-header')) {
  class ThemeHeader extends HTMLElement {
    connectedCallback() {
      this.header_section = document.querySelector('.header-section');
      this.menu = this.querySelector('#mobile-menu');
      this.toggle = document.querySelector('.mobile-toggle-wrapper');

      // The drawer must live outside the header: .header-section creates a
      // stacking context (sticky, z-index 50) that sits below the
      // click-capture (52), so a fixed drawer left inside it could never
      // paint above the dim. It is rendered inside the header section for
      // access to the section's settings/blocks, then teleported to
      // #wrapper next to the other side panels. Stale copies from previous
      // section renders (theme editor) are removed first.
      const wrapper = document.getElementById('wrapper');
      if (this.menu && wrapper) {
        document.querySelectorAll('.mobile-menu-drawer').forEach((el) => {
          if (el !== this.menu) el.remove();
        });
        wrapper.appendChild(this.menu);
      }
      this._scrollTicking = false;
      this._stickyOffset = 0;

      // The drawer opens through OverlayManager, which provides the
      // click-capture, scroll lock, focus trap and Escape handling.
      // The hamburger state is synced from the panel events so every
      // close path (capture click, Escape, close button) resets it.
      this.toggle.querySelector('.mobile-toggle').addEventListener('click', (e) => {
        e.preventDefault();
        if (OverlayManager.isOpen(this.menu)) {
          OverlayManager.close(this.menu);
        } else {
          OverlayManager.open(this.menu);
        }
        window.dispatchEvent(new Event('resize.resize-select'));
      });
      document.addEventListener('panel:open', (e) => {
        if (e.detail.panel === this.menu) {
          this.toggle.classList.add('active');
          // Measure the header again as the menu opens. Its position only
          // matters while the drawer is on screen, and anything that shifted
          // the layout since load (the announcement bar marquee collapsing,
          // late fonts, a dismissed bar) would otherwise leave the drawer
          // sitting in a gap below the header.
          this.setHeaderBottom();
        }
      });
      document.addEventListener('panel:closed', (e) => {
        if (e.detail.panel === this.menu) {
          this.toggle.classList.remove('active');
          this.toggle.removeAttribute('open');
        }
      });

      // Cache sticky offset and recalculate on resize
      this._cacheStickyOffset();
      window.addEventListener('resize', debounce(() => {
        this._cacheStickyOffset();
        this.setHeaderBottom();
      }, 100));

      // Single scroll listener with rAF throttle
      window.addEventListener('scroll', () => {
        if (!this._scrollTicking) {
          this._scrollTicking = true;
          requestAnimationFrame(() => {
            this.setStickyClass();
            this.setHeaderOffset();
            this.setHeaderHeight();
            this.setHeaderBottom();
            this._scrollTicking = false;
          });
        }
      }, { passive: true });

      window.dispatchEvent(new Event('scroll'));

      // Those vars are otherwise only refreshed on scroll, so they keep
      // whatever the layout was at load. The announcement bar marquee
      // collapses shortly after load (and late fonts can resize the header),
      // which left them measuring a taller header than the one on screen —
      // the mobile menu then opened a gap below it. Re-measure whenever the
      // header or the bar actually changes geometry. The bar is observed as
      // well because its collapse moves the header without resizing it.
      if ('ResizeObserver' in window) {
        this._headerResizeObserver = new ResizeObserver(() => {
          this.setHeaderOffset();
          this.setHeaderHeight();
          this.setHeaderBottom();
        });
        this._headerResizeObserver.observe(this);
        const a_bar_section = document.querySelector('.announcement-bar-section');
        if (a_bar_section) {
          this._headerResizeObserver.observe(a_bar_section);
        }
      }

      if (document.querySelector('.announcement-bar-section')) {
        window.addEventListener('scroll', () => this.setAnnouncementHeight(), {
          passive: true
        });
        window.dispatchEvent(new Event('resize'));
      }

      // Buttons.
      this.menu.querySelectorAll('summary').forEach(summary => summary.addEventListener('click', this.onSummaryClick.bind(this)));
      this.menu.querySelectorAll('.parent-link-back--button').forEach(button => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
    }

    _cacheStickyOffset() {
      if (this.classList.contains('header-sticky--active')) {
        // Skip recalculation while sticky — the header is position:sticky at top:0,
        // so getBoundingClientRect().top would return 0 and corrupt the offset.
        if (this.classList.contains('is-sticky')) {
          return;
        }
        this._stickyOffset = parseInt(this.header_section.getBoundingClientRect().top, 10) + document.documentElement.scrollTop;
      }
    }
    setStickyClass() {
      if (this.classList.contains('header-sticky--active')) {
        this.classList.toggle('is-sticky', window.scrollY >= this._stickyOffset && window.scrollY > 0);
      }
    }
    setAnnouncementHeight() {
      const a_bar = document.getElementById('shopify-section-announcement-bar');
      if (a_bar) {
        let h = a_bar.clientHeight;
        document.documentElement.style.setProperty('--announcement-height', h + 'px');
      }
    }
    disconnectedCallback() {
      // The theme editor re-renders the section — without this the old
      // observer keeps firing against a detached header.
      if (this._headerResizeObserver) {
        this._headerResizeObserver.disconnect();
        this._headerResizeObserver = null;
      }
    }
    setHeaderOffset() {
      let h = this.header_section.getBoundingClientRect().top;
      document.documentElement.style.setProperty('--header-offset', h + 'px');
    }
    setHeaderHeight() {
      let h = this.clientHeight;
      document.documentElement.style.setProperty('--header-height', h + 'px');
    }
    // The header's real bottom edge. --header-offset + --header-height only
    // approximates it: clientHeight drops the border, and neither accounts
    // for a transparent header being taken out of flow. Measure it directly
    // so the mobile menu can sit flush under the header. Clamped at 0 so a
    // non-sticky header scrolling away puts the menu at the viewport top.
    setHeaderBottom() {
      let b = this.getBoundingClientRect().bottom;
      document.documentElement.style.setProperty('--header-bottom', Math.max(0, b) + 'px');
    }
    onSummaryClick(event) {
      const summaryElement = event.currentTarget;
      const detailsElement = summaryElement.parentNode;
      const parentLinksEnabled = this.menu.dataset.parentLinks === 'true';

      // When parent links setting is on, only arrow opens submenu
      if (parentLinksEnabled) {
        event.preventDefault();
        const isArrowClick = event.target.closest('.link-forward');

        if (!isArrowClick) {
          const anchor = summaryElement.querySelector('a');
          if (anchor && anchor.href) {
            window.location.href = anchor.href;
          }
          return;
        }
        // Arrow clicked — manually open details
        detailsElement.setAttribute('open', '');
      }

      const parentMenuElement = detailsElement.closest('.link-container');

      // Stamp deferred template content on first open
      const tpl = detailsElement.querySelector(':scope > template.mobile-menu-deferred');
      if (tpl) {
        tpl.replaceWith(tpl.content.cloneNode(true));
        // Re-bind back buttons for the newly stamped content
        detailsElement.querySelectorAll('.parent-link-back--button').forEach(button => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
        detailsElement.querySelectorAll('details.link-container summary').forEach(summary => summary.addEventListener('click', this.onSummaryClick.bind(this)));
      }

      if (this.querySelector('.parent-link-back--button')) {
        this.menu.scrollTop = 0;
      }

      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
      }, 100);
    }
    onCloseButtonClick(event) {
      event.preventDefault();
      const detailsElement = event.currentTarget.closest('details');
      this.closeSubmenu(detailsElement);
    }
    closeSubmenu(detailsElement) {
      detailsElement.classList.remove('menu-opening');
      this.closeAnimation(detailsElement);
    }
    closeAnimation(detailsElement) {
      let animationStart;

      const handleAnimation = (time) => {
        if (animationStart === undefined) {
          animationStart = time;
        }

        const elapsedTime = time - animationStart;

        if (elapsedTime < 400) {
          window.requestAnimationFrame(handleAnimation);
        } else {
          detailsElement.removeAttribute('open');
        }
      };

      window.requestAnimationFrame(handleAnimation);
    }
  }
  customElements.define('theme-header', ThemeHeader);
}
/**
 *  @class
 *  @function FullMenu
 */
if (!customElements.get('full-menu')) {
  class FullMenu extends HTMLElement {
    connectedCallback() {
      this.submenus = this.querySelectorAll('.thb-full-menu>.menu-item-has-children:not(.menu-item-has-megamenu)>.sub-menu');
      this.bindDisclosure();

      if (!this.submenus.length) {
        return;
      }
      // resize on initial load
      window.addEventListener('resize', debounce(() => {
        this.resizeSubMenus();
      }, 100));

      window.dispatchEvent(new Event('resize'));

      document.fonts.ready.then(() => {
        this.resizeSubMenus();
      });
    }
    bindDisclosure() {
      if (this.disclosureBound) {
        return;
      }
      this.disclosureBound = true;

      this.addEventListener('click', (event) => {
        const button = event.target.closest('.thb-full-menu__toggle');
        if (!button || !this.contains(button)) {
          return;
        }
        event.preventDefault();
        this.toggleFromButton(button);
      });

      this.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') {
          return;
        }
        const item = event.target.closest('li.menu-item-has-children.is-open');
        if (!item || !this.contains(item)) {
          return;
        }
        event.preventDefault();
        const button = item.querySelector(':scope > .thb-full-menu__toggle');
        this.closeItem(item);
        if (button) {
          button.focus();
        }
      });

      this.addEventListener('focusout', (event) => {
        const item = event.target.closest('li.menu-item-has-children');
        if (!item || !this.contains(item)) {
          return;
        }
        const next = event.relatedTarget;
        if (next && item.contains(next)) {
          return;
        }
        if (!next) {
          window.requestAnimationFrame(() => {
            if (!item.contains(document.activeElement)) {
              this.closeItem(item);
            }
          });
          return;
        }
        this.closeItem(item);
      });

      this.onDocumentClick = (event) => {
        if (this.contains(event.target)) {
          return;
        }
        this.closeAll();
      };
      document.addEventListener('click', this.onDocumentClick);

      this.querySelectorAll('li.menu-item-has-children').forEach((item) => {
        item.addEventListener('mouseleave', () => {
          if (item.contains(document.activeElement)) {
            return;
          }
          this.closeItem(item);
        });
      });
    }
    toggleFromButton(button) {
      const item = button.closest('li.menu-item-has-children');
      if (!item) {
        return;
      }
      if (button.getAttribute('aria-expanded') === 'true') {
        this.closeItem(item);
        return;
      }
      this.closeSiblings(item);
      this.openItem(item);
    }
    openItem(item) {
      const button = item.querySelector(':scope > .thb-full-menu__toggle');
      if (button) {
        button.setAttribute('aria-expanded', 'true');
      }
      item.classList.add('is-open');
    }
    closeItem(item) {
      item.classList.remove('is-open');
      item.querySelectorAll('.thb-full-menu__toggle[aria-expanded="true"]').forEach((button) => {
        button.setAttribute('aria-expanded', 'false');
      });
      item.querySelectorAll('.is-open').forEach((openItem) => {
        openItem.classList.remove('is-open');
      });
    }
    closeSiblings(item) {
      const parent = item.parentElement;
      if (!parent) {
        return;
      }
      parent.querySelectorAll(':scope > li.menu-item-has-children.is-open').forEach((openItem) => {
        if (openItem !== item) {
          this.closeItem(openItem);
        }
      });
    }
    closeAll() {
      this.querySelectorAll('li.menu-item-has-children.is-open').forEach((item) => {
        this.closeItem(item);
      });
    }
    resizeSubMenus() {
      this.submenus.forEach((submenu) => {
        let sub_submenus = submenu.querySelectorAll(':scope >.menu-item-has-children>.sub-menu');

        sub_submenus.forEach((sub_submenu) => {
          let w = sub_submenu.offsetWidth,
            l = sub_submenu.parentElement.parentElement.getBoundingClientRect().left + sub_submenu.parentElement.parentElement.clientWidth + 10,
            total = w + l;
          if (total > document.body.clientWidth) {
            sub_submenu.parentElement.classList.add('left-submenu');
          } else if (sub_submenu.parentElement.classList.contains('left-submenu')) {
            sub_submenu.parentElement.classList.remove('left-submenu');
          }
        });
      });
    }
  }
  customElements.define('full-menu', FullMenu);
}
